import type { AssistantChartImageId, AssistantConversationId } from "@inrev/common";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useRequest } from "../../../utils/request";
import { assistantChartImageQueryKey } from "../assistant.keys";

/**
 * A chart PNG the agent drew, as an object URL an `<img>` can show. Fetched with the auth header,
 * which an `<img src>` pointed at the API could not send. A stored chart never changes.
 */
export const useFetchAssistantChartImage = (
	conversationId: AssistantConversationId,
	imageId: AssistantChartImageId,
) => {
	const { get } = useRequest();

	const query = useQuery({
		queryKey: assistantChartImageQueryKey(imageId),
		queryFn: async () =>
			await get(
				`/v2/admin/assistant/conversations/${conversationId}/charts/${imageId}`,
				undefined,
				"blob",
			),
		staleTime: Number.POSITIVE_INFINITY,
	});

	// Made and revoked in one effect, so a remount (as StrictMode does in development) cannot
	// revoke a URL the image still shows.
	const [url, setUrl] = useState<string | undefined>(undefined);
	useEffect(() => {
		if (query.data === undefined) return;
		const objectUrl = URL.createObjectURL(query.data);
		setUrl(objectUrl);
		return () => {
			URL.revokeObjectURL(objectUrl);
			setUrl(undefined);
		};
	}, [query.data]);

	return { ...query, url };
};
